import { z } from 'zod';
import { MAX_BYLINE_LENGTH, MAX_LOCATION_LENGTH } from './upsert/constants';
import { STORY_PARTICIPANT_CHOICES } from './types';

/**
 * A field the API leaves out when unset. A null is read the same way, so the
 * parsed topic only ever holds undefined for it.
 */
function absentAsUndefined<T extends z.ZodTypeAny>(schema: T) {
  return schema
    .nullish()
    .transform((value): z.output<T> | undefined => value ?? undefined);
}

export const TopicSchema = z
  .object({
    id: z.string(),
    title: z.string().min(1),
    body: z.string().max(2000),
    imageData: absentAsUndefined(z.string()),
    thumbnailImageData: absentAsUndefined(z.string()),
    startTime: z.string().datetime({ offset: true }),
    endTime: absentAsUndefined(z.string().datetime({ offset: true })),
    timezone: z.string().min(1),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    introCountLimit: z.number().int().positive().nullable().optional(),
    impressionGoal: z.number().int().positive().nullable().optional(),
    onlyManagersCanShare: z.boolean().optional(),
    introCount: z.number().int().nonnegative().optional(),
    managerEmails: z.array(z.string()).optional(),
    type: z
      .enum([
        'beautification',
        'challenge',
        'deal',
        'event',
        'rally',
        'news',
        'volunteering',
      ])
      .default('event'),
    byInviteOnly: z.boolean().optional(),
    customTemplates: absentAsUndefined(z.string()),
    // Type-specific payload (#447), stored opaquely. Passthrough keeps unknown
    // props opaque.
    props: z
      .object({
        exampleFieldValues: z.record(z.string()).optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    // The Settings tab (see Topic.reshareStorySettings). Passthrough keeps
    // settings this client doesn't know yet.
    reshareStorySettings: z
      .object({
        allowedDetailSettings: z
          .object({
            prefix: z.string().optional(),
            slotId: z.string(),
            suffix: z.string().optional(),
          })
          .passthrough()
          .optional(),
        participants: z.array(z.enum(STORY_PARTICIPANT_CHOICES)).optional(),
        startingSenderAppositive: z.string().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    byline: absentAsUndefined(z.string().max(MAX_BYLINE_LENGTH)),
    location: absentAsUndefined(z.string().max(MAX_LOCATION_LENGTH)),
  })
  .refine(d => !d.endTime || new Date(d.endTime) >= new Date(d.startTime), {
    message: 'endTime must be at or after startTime',
    path: ['endTime'],
  });
